import { useState, useEffect, useCallback, useRef } from 'react';

const TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes - PCI DSS compliant
const WARNING_MS = 13 * 60 * 1000; // 13 minutes - warn 2 min before expiry
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
const LAST_ACTIVITY_KEY = 'chamalink_last_activity';

export function useInactivityTimeout() {
  const [sessionExpired, setSessionExpired] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const timerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const hasExpiredRef = useRef(false);

  const resetTimer = useCallback(() => {
    if (hasExpiredRef.current) return;
    setShowWarning(false);
    localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (timerRef.current) clearTimeout(timerRef.current);
    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
    }, WARNING_MS);
    timerRef.current = setTimeout(() => {
      hasExpiredRef.current = true;
      setSessionExpired(true);
    }, TIMEOUT_MS);
  }, []);

  useEffect(() => {
    if (hasExpiredRef.current) return;

    // Always start fresh on mount - no stale timestamp checks
    resetTimer();

    ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, resetTimer, { passive: true });
    });

    const onStorage = (e) => {
      if (e.key === 'token' && !e.newValue) {
        hasExpiredRef.current = true;
        setSessionExpired(true);
      }
      if (e.key === LAST_ACTIVITY_KEY && e.newValue && !hasExpiredRef.current) {
        setShowWarning(false);
        if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
        if (timerRef.current) clearTimeout(timerRef.current);
        warningTimerRef.current = setTimeout(() => {
          setShowWarning(true);
        }, WARNING_MS);
        timerRef.current = setTimeout(() => {
          hasExpiredRef.current = true;
          setSessionExpired(true);
        }, TIMEOUT_MS);
      }
    };
    window.addEventListener('storage', onStorage);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      ACTIVITY_EVENTS.forEach(event => {
        document.removeEventListener(event, resetTimer);
      });
      window.removeEventListener('storage', onStorage);
    };
  }, [resetTimer]);

  const handleSessionLogout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    window.location.href = '/login';
  }, []);

  const dismissWarning = useCallback(() => {
    setShowWarning(false);
    resetTimer();
  }, [resetTimer]);

  return { sessionExpired, showWarning, handleSessionLogout, dismissWarning };
}
